import {useCallback, useEffect, useState} from 'react';
import {getProgramSectionTemplates} from '@/services/auditService/auditProgramTemplateService';
import {resolveTemplateContent} from '@/pages/components/audit-program/templates/defaults';

/**
 * Load published templates for a section; missing pages fall back to code defaults.
 */
export function useProgramSectionTemplates(sectionCode: string) {
    const [templates, setTemplates] = useState<Record<string, Record<string, unknown>>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fromApi = await getProgramSectionTemplates(sectionCode);
            setTemplates(fromApi ?? {});
        } catch (e: unknown) {
            // Offline / API missing — Program still works via code defaults
            setTemplates({});
            setError(e instanceof Error ? e.message : 'Failed to load program templates');
        } finally {
            setLoading(false);
        }
    }, [sectionCode]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const getContent = useCallback(
        (pageCode: string) => resolveTemplateContent(sectionCode, pageCode, templates[pageCode]),
        [sectionCode, templates],
    );

    return {templates, loading, error, reload, getContent};
}
